/**
 * N3 · Competência 6 (Estatística) · task-model H3.30.K02
 *
 *   "Construir um gráfico de colunas a partir de uma tabela."
 *
 * O caminho inverso da leitura: a tabela dá os valores, e o aluno levanta cada coluna
 * até a altura certa, arrastando o topo dela. As colunas encaixam nas linhas de grade
 * — de 2 em 2, com números só de 10 em 10 —, e acertar uma altura é contar linhas a
 * partir do número mais próximo.
 *
 * A figura é o mesmo `barras()` do item da tendência, desenhado sem colunas: eixos,
 * grade, categorias. As colunas são a camada arrastável.
 */

import type { Rng } from '../../core/rng.ts';
import { conta } from '../../core/pt.ts';
import type { Vec2 } from '../../geom/vec.ts';
import { barras } from '../../render/chart.ts';
import type { Shape } from '../../render/scene.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  GRAF_CONTAGEM_LINHAS,
  GRAF_LEITURA_ESCALA,
  GRAF_TROCOU_COLUNAS,
} from '../../schema/errors.ts';
import type { DragTarget, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type Inline } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

const W = 320;
const H = 180;
const Y_TOP = 40;
const GRADE = 2;
/** Unidades de desenho por unidade de valor. */
const POR_UNIDADE = H / Y_TOP;

interface Contexto {
  readonly texto: string;
  readonly titulo: string;
  readonly categoria: string;
  readonly medida: string;
  readonly eixo: string;
  readonly nomes: readonly string[];
}

const CONTEXTOS: readonly Contexto[] = [
  {
    texto:
      'Numa pesquisa, os alunos de uma escola escolheram o esporte preferido. A tabela mostra o resultado.',
    titulo: 'Esporte preferido',
    categoria: 'Esporte',
    medida: 'Alunos',
    eixo: 'Número de alunos',
    nomes: ['Futebol', 'Vôlei', 'Basquete', 'Natação', 'Handebol'],
  },
  {
    texto: 'Uma feira registrou quantos quilogramas de cada fruta foram vendidos num sábado.',
    titulo: 'Frutas vendidas',
    categoria: 'Fruta',
    medida: 'Quilogramas',
    eixo: 'Quilogramas vendidos',
    nomes: ['Banana', 'Maçã', 'Laranja', 'Uva', 'Manga'],
  },
  {
    texto: 'A biblioteca de um bairro anotou quantos livros emprestou em cada mês.',
    titulo: 'Livros emprestados',
    categoria: 'Mês',
    medida: 'Livros',
    eixo: 'Livros emprestados',
    nomes: ['Março', 'Abril', 'Maio', 'Junho', 'Julho'],
  },
];

export interface Params {
  readonly contexto: number;
  readonly n: number;
  readonly valores: readonly number[];
}

function drawParams(rng: Rng): Params {
  const n = rng.pick([4, 5]);
  // Valores pares de 4 a 38, distintos: cada um cai numa linha da grade, e poucos
  // coincidem com os números do eixo — é preciso contar linhas.
  const pool = Array.from({ length: 18 }, (_v, i) => (i + 2) * GRADE);
  const valores = rng.shuffle(pool).slice(0, n);
  return { contexto: rng.int(0, CONTEXTOS.length - 1), n, valores };
}

const slot = (n: number): number => W / n;
const xDe = (i: number, n: number): number => i * slot(n) + slot(n) / 2;

function alvoDe(p: Params): DragTarget {
  const alturas = (pos: readonly Vec2[]): number[] => pos.map((q) => Math.round(q.y / POR_UNIDADE));
  const certo = (hs: readonly number[], i: number): boolean => hs[i] === p.valores[i];
  const ordenados = (xs: readonly number[]): string => [...xs].sort((a, b) => a - b).join(',');
  const nomes = (CONTEXTOS[p.contexto] as Contexto).nomes;

  return {
    describe: [
      txt('Arraste o topo de cada coluna até a altura que corresponde ao valor da tabela.'),
    ],
    correto: (pos) => {
      const hs = alturas(pos);
      return p.valores.every((_v, i) => certo(hs, i));
    },
    diagnostics: [
      {
        error: GRAF_TROCOU_COLUNAS,
        test: (pos) => {
          const hs = alturas(pos);
          return ordenados(hs) === ordenados(p.valores);
        },
      },
      {
        // Todas as alturas multiplicadas pelo mesmo fator (diferente de 1).
        error: GRAF_LEITURA_ESCALA,
        test: (pos) => {
          const hs = alturas(pos);
          const f = (hs[0] as number) / (p.valores[0] as number);
          return (
            f > 0 &&
            Math.abs(f - 1) > 1e-6 &&
            hs.every((h, i) => Math.abs(h - f * (p.valores[i] as number)) < 1e-6)
          );
        },
      },
      {
        // Colunas certas ou a uma linha de grade do valor, com ao menos uma assim.
        error: GRAF_CONTAGEM_LINHAS,
        test: (pos) => {
          const hs = alturas(pos);
          const difs = hs.map((h, i) => Math.abs(h - (p.valores[i] as number)));
          return difs.every((d) => d === 0 || d === GRADE) && difs.some((d) => d === GRADE);
        },
      },
    ],
    partialCredit: p.valores.map((v, i) => ({
      id: `coluna-${i}`,
      label: `${nomes[i]}: coluna na altura ${v}`,
      weight: 1 / p.n,
      test: (pos: readonly Vec2[]) => certo(alturas(pos), i),
    })),
    analyze(pos) {
      const hs = alturas(pos);
      const out: Inline[] = [];
      p.valores.forEach((v, i) => {
        if (i > 0) out.push(br);
        out.push(
          strong(`${nomes[i]}: `),
          txt(
            certo(hs, i)
              ? `altura ${v}, certa.`
              : `você levou a coluna a ${hs[i]}; a tabela diz ${v}.`,
          ),
        );
      });
      return out;
    },
    reviewShapes(pos) {
      const hs = alturas(pos);
      const w = slot(p.n) * 0.56;
      return p.valores.flatMap((v, i): Shape[] =>
        certo(hs, i)
          ? []
          : [
              {
                t: 'rect',
                at: { x: xDe(i, p.n) - w / 2, y: 0, w, h: v * POR_UNIDADE },
                role: 'construction',
              },
            ],
      );
    },
    reviewLegend: [txt('Contorno tracejado: a altura certa das colunas que ficaram erradas.')],
  };
}

export const montarColunas: ItemTemplate<Params, null> = {
  id: templateId('h3-30-K02-montar-colunas'),
  version: 1,
  taskModel: taskModelCode('H3.30.K02'),
  skills: [skillCode('H3.30')],
  axis: 'II',
  level: 'N3',
  title: 'Construir um gráfico de colunas a partir de uma tabela',
  responseModel: 'GPCM',
  difficultySources: ['steps.2', 'two-supports'],
  params: drawParams,
  solve: () => null,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.n === 5) fontes.push('steps.3plus');
    return fontes;
  },
  build(p) {
    const ctx = CONTEXTOS[p.contexto] as Contexto;
    const nomes = ctx.nomes.slice(0, p.n);
    const base = barras({
      categorias: nomes,
      valores: nomes.map(() => 0),
      yTop: Y_TOP,
      yStep: 10,
      gridStep: GRADE,
      xLabel: ctx.categoria,
      yLabel: ctx.eixo,
      title: ctx.titulo,
      desc: '',
    });
    const tabelaTxt = nomes.map((nm, i) => `${nm}: ${p.valores[i]}`).join('; ');
    const descricao =
      `Gráfico de colunas "${ctx.titulo}" ainda sem as colunas, com o eixo vertical de 0 a ${Y_TOP}, números de 10 em 10 ` +
      `e linhas de grade de ${GRADE} em ${GRADE}. As categorias são ${nomes.join(', ')}; o topo de cada coluna pode ser ` +
      'arrastado para cima ou para baixo.';
    return {
      stem: [txt(ctx.texto)],
      comando: [
        txt(
          'Monte o gráfico de colunas com os dados da tabela: arraste o topo de cada coluna até a altura certa.',
        ),
      ],
      stimuli: [
        {
          kind: 'table',
          motion: 'none',
          audio: { need: 'none' },
          id: 'tabela',
          alt: `Tabela "${ctx.titulo}": ${tabelaTxt}.`,
          headers: [[txt(ctx.categoria)], [txt(ctx.medida)]],
          rows: nomes.map((nm, i) => [[txt(nm)], [txt(String(p.valores[i]))]]),
        },
      ],
      interaction: {
        kind: 'drag-points',
        descricao,
        backdrop: base.shapes,
        viewBox: base.viewBox,
        pontos: nomes.map((nm, i) => ({ nome: nm, inicial: { x: xDe(i, p.n), y: 0 } })),
        ligacoes: [],
        passo: GRADE * POR_UNIDADE,
        unidade: POR_UNIDADE,
        trava: 'y',
        desenho: 'barras',
        base: 0,
        larguraBarra: slot(p.n) * 0.56,
        limites: { x: 0, y: 0, w: W, h: Y_TOP * POR_UNIDADE },
        alvo: alvoDe(p),
      },
      rationale: [
        strong('Resolução. '),
        txt(`No eixo vertical, os números vão de 10 em 10 e cada linha da grade vale ${GRADE}. `),
        txt(
          'Para cada valor, parte-se do número do eixo logo abaixo e contam-se as linhas que faltam: ',
        ),
        txt(
          nomes
            .map((nm, i) => {
              const v = p.valores[i] as number;
              const dezena = Math.floor(v / 10) * 10;
              const linhas = (v - dezena) / GRADE;
              return linhas === 0
                ? `${nm}, ${v} (na linha do ${dezena})`
                : `${nm}, ${v} (${dezena} e mais ${conta(linhas, 'linha')})`;
            })
            .join('; ') + '.',
        ),
      ],
      declared: 'facil',
    };
  },
};
